import type { AdminJob } from '@hemmafixa/shared';
import {
  Avatar,
  Badge,
  Button,
  Card,
  DescriptionList,
  DetailLayout,
  DetailSection,
  EmptyState,
  FormField,
  InlineConfirm,
  InlinePanel,
  InlinePanelFooter,
  StatusBadge,
  Textarea,
  toast,
} from '@hemmafixa/ui';
import { Pencil, Plus, SearchX, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { fill, useI18n } from '@/i18n/context';
import type { Messages } from '@/i18n/sv';
import { ApiError } from '@/lib/api';
import { BackLink } from '../shared/back-link';
import { formatPostalCode, useCategoryLabel } from '../shared/format';
import { JobEditForm } from '../shared/job-edit-form';
import { JobLocationMap } from '../shared/job-location-map';
import { JobFiles } from '../shared/job-files';
import { JobQuestionList } from '../shared/job-questions';
import { QueryView } from '../shared/query-view';
import { useStatuses } from '../shared/statuses';
import { useApproveJob, useEditJob, useRejectJob, useRequestJobInfo, useReviewJob } from './api';

type Panel = 'approve' | 'ask' | 'reject' | 'edit' | null;

function actionError(error: unknown, t: Messages) {
  if (error instanceof ApiError && error.code === 'CONFLICT') return t.admin.review.conflict;
  if (error instanceof ApiError && error.status === 0) return t.common.errorMessage;
  return t.admin.review.genericError;
}

/** One job in the review queue: read it, correct it, then approve, ask the client or reject (P3-10). */
export function ReviewJobPage() {
  const { id = '' } = useParams();
  const { t } = useI18n();

  return (
    <div className="grid gap-4">
      <BackLink to="/admin/review" label={t.nav.review} />
      <ReviewJobDetail id={id} />
    </div>
  );
}

/** The job with its review actions; the full page and the review queue's details panel both use it. */
export function ReviewJobDetail({ id }: { id: string }) {
  const { t } = useI18n();
  const query = useReviewJob(id);
  return (
    <QueryView query={query} loading="detail">
      {(job) =>
        !job ? (
          <EmptyState icon={SearchX} title={t.common.notFoundTitle} description={t.common.notFoundText} />
        ) : (
          <ReviewJob job={job} />
        )
      }
    </QueryView>
  );
}

function ReviewJob({ job }: { job: AdminJob }) {
  const { t, date } = useI18n();
  const statuses = useStatuses();
  const categoryLabel = useCategoryLabel();
  const [panel, setPanel] = useState<Panel>(null);
  const approve = useApproveJob(job.id);
  const edit = useEditJob(job.id);
  const r = t.admin.review;

  const inReview = job.status === 'in_review';
  const canEdit = inReview || job.status === 'needs_info';

  const runApprove = () =>
    approve.mutate(undefined, {
      onSuccess: () => {
        setPanel(null);
        toast.success(r.approvedToast);
      },
      onError: (error) => toast.error(actionError(error, t)),
    });

  // Approve, ask and reject open in place of the action buttons
  const actionPanel =
    panel === 'approve' ? (
      <InlineConfirm
        open
        onOpenChange={(open) => !open && setPanel(null)}
        title={r.approveTitle}
        description={r.approveText}
        confirmLabel={r.approve}
        cancelLabel={t.common.cancel}
        loading={approve.isPending}
        onConfirm={runApprove}
      />
    ) : panel === 'ask' ? (
      <AskPanel jobId={job.id} onClose={() => setPanel(null)} />
    ) : panel === 'reject' ? (
      <RejectPanel jobId={job.id} onClose={() => setPanel(null)} />
    ) : null;

  const facts = [
    { label: r.columns.status, value: <StatusBadge status={job.status} statuses={statuses.job} /> },
    ...(job.submittedAt ? [{ label: r.columns.received, value: date(job.submittedAt) }] : []),
    { label: t.job.wantedStart, value: t.wantedStart[job.wantedStart] },
    ...(job.rejectionReason ? [{ label: r.rejectionReason, value: job.rejectionReason }] : []),
  ];
  const editButton = canEdit && panel !== 'edit' && (
    <Button
      variant={inReview ? 'outline' : 'primary'}
      className={inReview ? undefined : 'min-w-32'}
      onClick={() => setPanel('edit')}
    >
      {r.edit}
    </Button>
  );
  const rejectButton = (
    <Button variant="outline" className="text-danger" onClick={() => setPanel('reject')}>
      {r.reject}
    </Button>
  );

  return (
    <>
      <Card className="grid gap-4 p-5">
        {/* Title on the left, the key facts beside it, so the card stays short */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
          <div className="flex min-w-0 flex-1 basis-64 items-center gap-4">
            <Avatar seed={job.id} name={job.title} kind="company" size="lg" />
            <div className="grid min-w-0 gap-0.5">
              <h1 className="truncate text-2xl font-bold text-secondary" title={job.title}>
                {job.title}
              </h1>
              <p className="truncate text-sm text-muted-foreground">
                {[categoryLabel(job.categorySlug), formatPostalCode(job.postalCode)]
                  .filter(Boolean)
                  .join(' • ')}
              </p>
            </div>
          </div>
          <dl className="flex flex-wrap gap-x-8 gap-y-3">
            {facts.map((fact) => (
              <div key={fact.label} className="grid gap-1.5">
                <dt className="text-xs text-muted-foreground">{fact.label}</dt>
                <dd className="text-sm font-medium text-secondary">{fact.value}</dd>
              </div>
            ))}
          </dl>
          {/* With three review actions below, editing sits up here so the buttons stay on one row */}
          {/* In review the actions sit in this row too, so the card has no second row */}
          {inReview && !actionPanel && (
            <div className="ml-auto flex flex-wrap justify-end gap-2">
              {canEdit && panel !== 'edit' && (
                <Button variant="outline" onClick={() => setPanel('edit')}>
                  <Pencil aria-hidden />
                  {r.edit}
                </Button>
              )}
              {rejectButton}
              <Button variant="outline" onClick={() => setPanel('ask')}>
                {r.ask}
              </Button>
              <Button className="min-w-28" onClick={() => setPanel('approve')}>
                {r.approve}
              </Button>
            </div>
          )}
        </div>
        {!actionPanel && !inReview && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
            {job.status === 'needs_info' && (
              <p className="min-w-0 flex-1 basis-56 rounded-xl bg-muted px-4 py-2.5 text-sm text-muted-foreground">
                {r.waitingForClient}
              </p>
            )}
            {job.status === 'needs_info' ? (
              <div className="ml-auto flex shrink-0 gap-2">
                {rejectButton}
                {editButton}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{r.reviewed}</p>
            )}
          </div>
        )}
        {actionPanel}
      </Card>
      <DetailLayout
        aside={
          <>
            {job.client && (
              <DetailSection title={r.contact} collapsible>
                <div className="grid gap-3">
                  {job.client.status === 'suspended' && (
                    <Badge tone="danger" className="justify-self-start">
                      {r.accountSuspended}
                    </Badge>
                  )}
                  <DescriptionList
                    items={[
                      { label: t.admin.users.columns.name, value: job.client.name },
                      {
                        label: r.email,
                        value: (
                          <a
                            className="break-all font-medium underline underline-offset-2"
                            href={`mailto:${job.client.email}`}
                          >
                            {job.client.email}
                          </a>
                        ),
                      },
                      ...(job.client.phone
                        ? [
                            {
                              label: r.phone,
                              value: (
                                <a
                                  className="font-medium underline underline-offset-2"
                                  href={`tel:${job.client.phone}`}
                                >
                                  {job.client.phone}
                                </a>
                              ),
                            },
                          ]
                        : []),
                    ]}
                  />
                  <Link
                    to={`/admin/users/${job.client.id}`}
                    className="justify-self-start text-sm font-medium underline underline-offset-2"
                  >
                    {t.admin.providers.openAccount}
                  </Link>
                </div>
              </DetailSection>
            )}
            <JobLocationMap
              location={job.location}
              postalCode={job.postalCode}
              className="h-56 overflow-hidden rounded-xl shadow-sm"
            />
          </>
        }
      >
        {panel === 'edit' ? (
          <JobEditForm job={job} description={r.editText} mutation={edit} onClose={() => setPanel(null)} />
        ) : (
          <DetailSection title={t.job.description}>
            <p className="whitespace-pre-line text-sm leading-relaxed">{job.description}</p>
          </DetailSection>
        )}
        <DetailSection title={r.questions}>
          <JobQuestionList questions={job.questions} />
        </DetailSection>
        <DetailSection title={r.history}>
          <ol className="grid gap-3 text-sm">
            {job.history.map((entry, index) => (
              <li key={index} className="grid gap-0.5">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge status={entry.toStatus} statuses={statuses.job} />
                  <span className="text-muted-foreground">
                    {entry.actorName ?? r.system} · {date(entry.createdAt)}
                  </span>
                </div>
                {entry.note && <p className="text-muted-foreground">{entry.note}</p>}
              </li>
            ))}
          </ol>
        </DetailSection>
        <DetailSection title={t.files.title}>
          <JobFiles files={job.attachments} />
        </DetailSection>
      </DetailLayout>
    </>
  );
}

function AskPanel({ jobId, onClose }: { jobId: string; onClose: () => void }) {
  const { t } = useI18n();
  const r = t.admin.review;
  const ask = useRequestJobInfo(jobId);
  const [questions, setQuestions] = useState(['']);
  const [errors, setErrors] = useState<(string | undefined)[]>([]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const found = questions.map((q) => (q.trim().length < 5 ? r.questionTooShort : undefined));
    setErrors(found);
    if (found.some(Boolean)) return;
    ask.mutate(
      questions.map((q) => q.trim()),
      {
        onSuccess: () => {
          onClose();
          toast.success(r.askedToast);
        },
        onError: (error) => toast.error(actionError(error, t)),
      },
    );
  };

  return (
    <InlinePanel
      title={r.askTitle}
      description={r.askText}
      onClose={onClose}
      onSubmit={submit}
      closeLabel={t.common.cancel}
    >
      {questions.map((question, index) => (
        <div key={index} className="flex items-start gap-2">
          <FormField
            label={fill(r.question, { number: index + 1 })}
            error={errors[index]}
            required
            className="flex-1"
          >
            <Textarea
              rows={2}
              maxLength={300}
              value={question}
              onChange={(event) =>
                setQuestions((list) => list.map((q, i) => (i === index ? event.target.value : q)))
              }
            />
          </FormField>
          {questions.length > 1 && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="mt-6"
              aria-label={fill(r.removeQuestion, { number: index + 1 })}
              onClick={() => {
                setQuestions((list) => list.filter((_, i) => i !== index));
                setErrors((list) => list.filter((_, i) => i !== index));
              }}
            >
              <Trash2 aria-hidden />
            </Button>
          )}
        </div>
      ))}
      {questions.length < 5 && (
        <Button
          type="button"
          variant="outline"
          className="justify-self-start"
          onClick={() => setQuestions((list) => [...list, ''])}
        >
          <Plus aria-hidden />
          {r.addQuestion}
        </Button>
      )}
      <InlinePanelFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t.common.cancel}
        </Button>
        <Button type="submit" loading={ask.isPending}>
          {r.sendQuestion}
        </Button>
      </InlinePanelFooter>
    </InlinePanel>
  );
}

function RejectPanel({ jobId, onClose }: { jobId: string; onClose: () => void }) {
  const { t } = useI18n();
  const r = t.admin.review;
  const reject = useRejectJob(jobId);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string>();

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (reason.trim().length < 3) {
      setError(r.reasonTooShort);
      return;
    }
    reject.mutate(reason.trim(), {
      onSuccess: () => {
        onClose();
        toast.success(r.rejectedToast);
      },
      onError: (failure) => toast.error(actionError(failure, t)),
    });
  };

  return (
    <InlinePanel
      title={r.rejectTitle}
      description={r.rejectText}
      onClose={onClose}
      onSubmit={submit}
      tone="danger"
      closeLabel={t.common.cancel}
    >
      <FormField label={r.reason} error={error} required>
        <Textarea
          rows={4}
          maxLength={500}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </FormField>
      <InlinePanelFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t.common.cancel}
        </Button>
        <Button type="submit" variant="danger" loading={reject.isPending}>
          {r.reject}
        </Button>
      </InlinePanelFooter>
    </InlinePanel>
  );
}
